import React, { useState, useRef, useEffect } from 'react';
import { Bot, Send, X, MessageSquare, Loader2, Lock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Detect whether the visitor has any kind of session in the app.
 * Signed-in == any of: admin, carrier/driver, or a started free trial.
 * Anonymous visitors are gated out of the AI chat to stop burning OpenAI
 * credit on traffic that can't convert today.
 */
const detectSignedIn = () => {
  try {
    if (typeof window === 'undefined') return { signedIn: false, identity: null };
    const admin = sessionStorage.getItem('adminLoggedIn') === 'true';
    const driverEmail = localStorage.getItem('driverEmail');
    const driverId = localStorage.getItem('driverId');
    const trialEmail = localStorage.getItem('trialBannerEmail');
    if (admin) return { signedIn: true, identity: { role: 'admin' } };
    if (driverEmail && driverId) {
      return { signedIn: true, identity: { role: 'carrier', email: driverEmail, id: driverId } };
    }
    if (trialEmail) return { signedIn: true, identity: { role: 'trial', email: trialEmail } };
    return { signedIn: false, identity: null };
  } catch {
    return { signedIn: false, identity: null };
  }
};

const FloatingChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [{ signedIn, identity }, setAuth] = useState(() => detectSignedIn());
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm the Tempus AI assistant. How can I help you find loads or carriers today?",
      timestamp: new Date().toISOString()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(`widget_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);

  // Re-check auth when the widget is opened (covers post-load login)
  useEffect(() => {
    if (isOpen) setAuth(detectSignedIn());
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  // Only nudge signed-in visitors — no point teasing anonymous traffic that
  // can't use the chat anyway.
  useEffect(() => {
    if (!signedIn) return;
    const timer = setTimeout(() => { setUnreadCount(1); }, 5000);
    return () => clearTimeout(timer);
  }, [signedIn]);

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;
    if (!signedIn) return; // hard guard — should never fire because input is replaced

    const userMessage = { role: 'user', content: input, timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch(`${API}/api/ai-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage.content,
          session_id: sessionId,
          user_role: identity?.role || 'unknown',
          user_email: identity?.email || null,
        }),
      });
      const data = await response.json();
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: data.response || data.message || "I'm here to help! What would you like to know?",
        timestamp: new Date().toISOString(),
      }]);
    } catch (error) {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: "Thanks for reaching out! Please check out our Load Board for available freight, or sign up as a driver to get matched with loads automatically.",
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const quickQuestions = [
    "How do I find loads?",
    "What are current rates?",
    "How do I sign up as a driver?"
  ];

  // ---------------------------------------------- collapsed bubble
  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50" data-testid="floating-chat-widget">
        <button
          onClick={() => { setIsOpen(true); setUnreadCount(0); }}
          className="relative w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
          data-testid="floating-chat-bubble"
          aria-label={signedIn ? 'Open Tempus AI chat' : 'Sign in to chat with Tempus AI'}
        >
          <MessageSquare className="w-6 h-6" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
              {unreadCount}
            </span>
          )}
        </button>
        {signedIn && (
          <div className="absolute bottom-16 right-0 bg-white text-zinc-800 px-4 py-2 rounded-lg shadow-lg text-sm whitespace-nowrap animate-bounce">
            Need help? Chat with us!
          </div>
        )}
      </div>
    );
  }

  // ---------------------------------------------- expanded panel — auth gate
  if (!signedIn) {
    return (
      <div
        className="fixed bottom-6 right-6 z-50 w-80 sm:w-96 shadow-2xl rounded-2xl overflow-hidden"
        data-testid="chat-signin-gate"
      >
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Lock className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Sign in to chat</h3>
              <p className="text-blue-100 text-xs">Tempus AI is free for members</p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="text-white/80 hover:text-white p-1"
            aria-label="Close"
            data-testid="chat-signin-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="bg-zinc-900 p-5 space-y-4">
          <p className="text-sm text-zinc-300">
            The Tempus AI assistant is available to carriers, brokers, and
            shippers who have signed up. Pick the door that fits you:
          </p>
          <div className="grid grid-cols-1 gap-2">
            <a
              href="/driver-signup"
              className="block bg-amber-500 hover:bg-amber-400 text-zinc-950 text-sm font-semibold px-4 py-2.5 rounded-lg text-center transition-colors"
              data-testid="chat-signin-carrier"
            >
              I drive trucks — sign me up (6%)
            </a>
            <a
              href="/pricing"
              className="block bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-4 py-2.5 rounded-lg text-center transition-colors"
              data-testid="chat-signin-broker"
            >
              I&apos;m a broker / shipper — start free trial
            </a>
            <a
              href="/driver-login"
              className="block bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs px-4 py-2 rounded-lg text-center transition-colors"
              data-testid="chat-signin-existing"
            >
              Already have an account → sign in
            </a>
          </div>
          <p className="text-[11px] text-zinc-500 text-center">
            We gate the AI chat to keep it fast for paying members.
          </p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------- expanded panel — authed
  return (
    <div
      className="fixed bottom-6 right-6 z-50 w-80 sm:w-96 shadow-2xl rounded-2xl overflow-hidden"
      data-testid="chat-panel"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Tempus AI</h3>
            <p className="text-blue-100 text-xs flex items-center gap-1">
              <span className="w-2 h-2 bg-green-400 rounded-full"></span> Online now
            </p>
          </div>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="text-white/80 hover:text-white p-1"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="h-80 bg-zinc-900 overflow-y-auto p-4 space-y-3">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] p-3 rounded-2xl text-sm ${
              msg.role === 'user'
                ? 'bg-blue-600 text-white rounded-br-md'
                : 'bg-zinc-800 text-zinc-100 rounded-bl-md'
            }`}>
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-zinc-800 p-3 rounded-2xl rounded-bl-md">
              <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Questions */}
      {messages.length <= 2 && (
        <div className="bg-zinc-800 px-4 py-2 flex gap-2 overflow-x-auto">
          {quickQuestions.map((q, idx) => (
            <button
              key={idx}
              onClick={() => setInput(q)}
              className="text-xs bg-zinc-700 hover:bg-zinc-600 text-zinc-300 px-3 py-1.5 rounded-full whitespace-nowrap transition-colors"
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <form onSubmit={sendMessage} className="bg-zinc-800 p-3 border-t border-zinc-700">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 bg-zinc-700 text-white placeholder-zinc-400 px-4 py-2 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loading}
            data-testid="chat-input"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="w-10 h-10 bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-600 text-white rounded-full flex items-center justify-center transition-colors"
            data-testid="chat-send-btn"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default FloatingChatWidget;
